import {useCallback, useEffect, useRef, useState} from 'react'
import {shallowEqual, useSelector} from 'react-redux'
import {RootState} from '../../../setup'
import {UserModel} from '../../modules/auth/models/UserModel'
import {useLayout} from '../../../_metronic/layout/core'
import {CreateExpertiseModal} from '../managePartners/components/modals/CreateExpertiseModal'
import {RescheduleModal} from './components/RescheduleModal'
import {LoaderOverlay} from '../../hooks/loader/LoaderOverlay'
import {
  acceptRequest,
  declineRequest,
  getMyRequestCounters,
  getMyRequests,
  rescheduleRequest,
  MyRequestTab,
} from '../../services/expertiseAdminService'
import {showErrorAlert} from '../../helpers/alert'
import {handleApiError} from '../../utils/handleApiError'
import Pagination from '../components/Pagination'
import {ExpertiseItemCard} from './components/ExpertiseItemCard'

/**
 * /my-expertise lista las SOLICITUDES que los miembros hacen al experto (ExpertiseRequest), no los
 * servicios que el publica. Son dos procesos distintos: la publicacion de un servicio la modera un
 * admin; una solicitud la responde el propio experto.
 *
 *   Request          -> status=request   (esperando su respuesta, aun no vencidas)
 *   Open Expertise   -> status=accepted
 *   Closed Expertise -> status=closed    (rechazadas + vencidas sin responder)
 */
const TABS: {id: string; label: string; status: MyRequestTab}[] = [
  {id: 'request', label: 'Request', status: 'request'},
  {id: 'open', label: 'Open Expertise', status: 'accepted'},
  {id: 'closed', label: 'Closed Expertise', status: 'closed'},
]

const LIMIT = 10

export const MyExpertisePage = () => {
  const {config, setLayout} = useLayout()
  const restoreRef = useRef(config)
  const user = useSelector<RootState>(({auth}) => auth.user, shallowEqual) as UserModel

  const [showCreate, setShowCreate] = useState(false)
  const [rescheduleTarget, setRescheduleTarget] = useState<any>(null)
  const [savingReschedule, setSavingReschedule] = useState(false)

  const [activeTab, setActiveTab] = useState('request')
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState<any[]>([])
  const [count, setCount] = useState(0)
  const [pendingCount, setPendingCount] = useState(0)

  const totalPages = Math.max(1, Math.ceil(count / LIMIT))

  const load = useCallback(async (tabId: string, page: number) => {
    const tab = TABS.find((t) => t.id === tabId) ?? TABS[0]
    try {
      setLoading(true)
      const data = await getMyRequests({
        status: tab.status,
        limit: LIMIT,
        offset: (page - 1) * LIMIT,
      })
      setItems(data?.results ?? [])
      setCount(data?.count ?? 0)
    } catch (error: any) {
      handleApiError(error)
      setItems([])
      setCount(0)
    } finally {
      setLoading(false)
    }
  }, [])

  const refreshBadge = useCallback(() => {
    // El badge sale de los contadores, no del listado: debe reflejar el total de pendientes aunque
    // estes viendo otra pestana o la pagina 3.
    getMyRequestCounters()
      .then((c) => setPendingCount(c?.pending ?? 0))
      .catch(() => setPendingCount(0))
  }, [])

  useEffect(() => {
    load(activeTab, currentPage)
  }, [activeTab, currentPage, load])

  useEffect(() => {
    refreshBadge()
  }, [refreshBadge])

  useEffect(() => {
    restoreRef.current = config
    setLayout({...config, header: {...config.header, left: 'page-title'}})

    // #kt_wrapper vive fuera del arbol de esta pagina, asi que el override se aplica marcando el
    // <body> mientras la pantalla esta montada. Se limpia al desmontar para no filtrar el padding
    // a otras rutas.
    document.body.classList.add('my-expertise-wrapper')

    return () => {
      document.body.classList.remove('my-expertise-wrapper')
      setLayout(restoreRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const reload = () => {
    load(activeTab, currentPage)
    refreshBadge()
  }

  const respond = async (action: () => Promise<any>, done: string) => {
    try {
      await action()
      showErrorAlert({title: 'Success', message: done})
      reload()
    } catch (error: any) {
      handleApiError(error)
    }
  }

  const handleAccept = (item: any) => respond(() => acceptRequest(item.id), 'Request accepted')

  const handleReject = (item: any) => respond(() => declineRequest(item.id), 'Request rejected')

  const submitReschedule = async (proposedOn: string) => {
    if (!rescheduleTarget) return
    try {
      setSavingReschedule(true)
      await rescheduleRequest(rescheduleTarget.id, proposedOn)
      // No queda reagendada todavia: la propuesta espera a que la otra parte la confirme.
      showErrorAlert({
        title: 'Sent',
        message: 'Your proposed date was sent. It applies once the other party confirms it.',
      })
      setRescheduleTarget(null)
      reload()
    } catch (error: any) {
      handleApiError(error)
    } finally {
      setSavingReschedule(false)
    }
  }

  const changeTab = (tabId: string) => {
    // Volver a la pagina 1: si estabas en la 3 y el nuevo filtro tiene una sola, la respuesta
    // vendria vacia y pareceria que no hay resultados.
    setActiveTab(tabId)
    setCurrentPage(1)
  }

  return (
    <div className='nb-my-expertise'>
      <LoaderOverlay visible={loading} message={'Loading...'} />

      <div className='nb-my-expertise__header'>
        <h1 className='nb-my-expertise__title'>My Expertise</h1>
        <button
          type='button'
          className='nb-my-expertise__action'
          onClick={() => setShowCreate(true)}
        >
          <span>Manage Expertise</span>
          <svg width='14' height='4' viewBox='0 0 14 4' fill='none' aria-hidden='true'>
            <path d='M8.5 0.382812L12 3.38696H0' stroke='white' />
          </svg>
        </button>
      </div>

      {/* Mismo marcado de tabs que HostPartnerExperiencePage y AdminDashboardPage, para heredar
          los estilos ya existentes de nav-line-tabs y el separador vertical. */}
      <ul className='nav nav-tabs nav-line-tabs nav-stretch fs-6 border-bottom-0'>
        {TABS.map((tab, index) => (
          <div className='d-flex flex-row cursor-pointer' key={`tabs-my-expertise-${index}`}>
            <li className='nav-item'>
              <a
                className={`nav-link text-capitalize ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => changeTab(tab.id)}
              >
                {tab.label}
                {tab.id === 'request' && (
                  <div className={`badge-wrapper ${pendingCount > 0 ? 'd-flex' : 'd-none'}`}>
                    <div className='badge-count'>{pendingCount}</div>
                  </div>
                )}
              </a>
            </li>
            <li className={`separator-vertical ${index < TABS.length - 1 ? 'd-flex' : 'd-none'}`}>
              <hr />
            </li>
          </div>
        ))}
      </ul>

      <div className='nb-my-expertise__list'>
        {!loading && items.length === 0 && (
          <div className='tap-host-secction-experiences-past-img-title justify-content-start text-dark'>
            No requests available.
          </div>
        )}
        {items.map((item) => (
          <ExpertiseItemCard
            key={item.id}
            item={item}
            // Las acciones solo aplican a lo que sigue esperando respuesta; en Open y Closed la
            // card queda de solo lectura.
            {...(activeTab === 'request'
              ? {
                  onAccept: handleAccept,
                  onReschedule: (it: any) => setRescheduleTarget(it),
                  onReject: handleReject,
                }
              : {})}
          />
        ))}
      </div>

      {/* El servicio cuelga del Partner, no del User: de ahi que se pase `partnerId`. */}
      <CreateExpertiseModal
        show={showCreate}
        close={() => setShowCreate(false)}
        partner={{id: user?.partnerId, companyName: user?.firstName}}
        onCreated={reload}
      />

      <RescheduleModal
        show={Boolean(rescheduleTarget)}
        close={() => setRescheduleTarget(null)}
        currentDate={rescheduleTarget?.scheduledOn}
        onSubmit={submitReschedule}
        loading={savingReschedule}
      />

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  )
}

export default MyExpertisePage
